import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { FollowUpImportance, type ILeadFollowUpRequest } from '../../viewModels/queue';
import { queryKeys } from './queryKeys';

export interface ScheduleFollowUpVariables {
	/** Company id for the lead the follow-up is being scheduled against. */
	companyId: string;
	/** Contact ids to attach the follow-up to (legacy attaches all lead contacts). */
	contactIds: string[];
	/** Concrete follow-up date-time (ISO string or Date). */
	date: string | Date;
	/** Free-text description the rep enters. */
	name: string;
	/** Spice level. Defaults to Low to match the legacy `setFollowUp` default. */
	importance?: FollowUpImportance;
}

/**
 * Schedules a standalone follow-up — i.e. with no call/outcome attached.
 *
 * Mirrors the legacy `QueueViewModel.setFollowUp` (`aida/viewModels/queue.ts`):
 * `PUT lead/{companyId}/followUp` with a concrete `followUpDate`. This is the
 * same backend path the standard Coffee UI uses when a rep schedules a
 * follow-up outside of a call, so no backend change is required.
 *
 * Unlike `useSaveCallOutcome`'s embedded follow-up (which resolves the curated
 * `followUpOptionId` shortcuts server-side), this hook only sends an explicit
 * date — matching legacy, where the standalone entry-point is the custom
 * date/time modal and the quick options are reserved for outcome saves.
 *
 * On success the focused-view lead cache is invalidated so the lead's
 * follow-up state refreshes.
 */
export function useScheduleFollowUp(): UseMutationResult<void, Error, ScheduleFollowUpVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<void, Error, ScheduleFollowUpVariables>({
		mutationFn: async ({ companyId, contactIds, date, name, importance = FollowUpImportance.Low }) => {
			if (!companyId) {
				throw new Error('useScheduleFollowUp: companyId is required');
			}

			const body: ILeadFollowUpRequest = {
				followUpContactIds: contactIds,
				followUpDate: date,
				followUpImportanceLevel: importance,
				followUpName: name,
				scheduledFollowUp: true,
			};

			const result = await userSession.webServiceHelper.callWebServiceAsync(
				ImpersonationBroker.composeApiUrl({ urlPath: `lead/${companyId}/followUp` }),
				'PUT',
				body
			);

			if (!result.success) {
				throw asApiError(result);
			}
		},
		onSuccess: (_void, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.lead(variables.companyId) });
		},
	});
}
